<template>
  <div class="container-xxl committees-page py-5">
    <div class="container">
      <section class="committees-hero">
        <div>
          <h6 class="section-title text-start text-primary text-uppercase">Kurullar</h6>
          <h1>Derneğin çalışma kurulları ve ilçe temsil ekipleri</h1>
          <p>
            Başkent Ağrılılar Derneği; sosyal destekten eğitime, esnaf dayanışmasından ilçe
            temsil kurullarına kadar çalışmalarını gönüllü ekiplerle yürütür.
          </p>
        </div>
        <div class="committees-summary">
          <span>{{ KURULS.length }}</span>
          <strong>Aktif kurul</strong>
          <small>Görev, sorumlu ve iletişim bilgileri</small>
        </div>
      </section>

      <div class="committees-toolbar">
        <div class="committees-search">
          <i class="fa fa-search"></i>
          <input
            v-model.trim="searchTerm"
            type="search"
            placeholder="Kurul, başkan, görev veya ilçe ara"
            aria-label="Kurullarda ara"
          >
        </div>
        <a :href="whatsappJoinUrl" target="_blank" rel="noopener" class="btn btn-primary">
          <i class="fab fa-whatsapp me-2"></i>Gönüllü Ol
        </a>
      </div>

      <section class="committees-feature-grid">
        <article>
          <i class="fa fa-map-marked-alt"></i>
          <h2>{{ representativeCount }}</h2>
          <p>İlçe temsil kurulu</p>
        </article>
        <article>
          <i class="fa fa-hands-helping"></i>
          <h2>Sosyal destek</h2>
          <p>Yardım, taziye, istihdam, eğitim ve aile destekleri için görev paylaşımı.</p>
        </article>
        <article>
          <i class="fa fa-users-cog"></i>
          <h2>Şeffaf görevler</h2>
          <p>Her kurulun sorumlusu, ekibi ve temel görevleri tek sayfada görülebilir.</p>
        </article>
      </section>

      <div class="committees-list">
        <article
          v-for="kurul in filteredKuruls"
          :key="kurul.isim"
          class="committee-card"
        >
          <div class="committee-card-head">
            <div>
              <span class="committee-type">{{ getCommitteeType(kurul.isim) }}</span>
              <h2>{{ kurul.isim }}</h2>
            </div>
            <a
              v-if="kurul.iletisim"
              :href="`tel:${normalizePhone(kurul.iletisim)}`"
              class="committee-phone"
            >
              <i class="fa fa-phone-alt"></i>
              {{ kurul.iletisim }}
            </a>
          </div>

          <div class="committee-lead">
            <i class="fa fa-user"></i>
            <span>Başkan / Sorumlu</span>
            <strong>{{ normalizeName(kurul.baskan) || 'Belirtilmedi' }}</strong>
          </div>

          <div class="committee-body">
            <div>
              <h3>Görevler</h3>
              <ul>
                <li v-for="gorev in kurul.gorevler" :key="gorev">{{ gorev }}</li>
              </ul>
            </div>
            <div v-if="kurul.ekip && kurul.ekip.length" class="committee-members">
              <h3>Kurul üyeleri</h3>
              <div>
                <span v-for="uye in kurul.ekip" :key="uye">{{ normalizeName(uye) }}</span>
              </div>
            </div>
          </div>
        </article>

        <div v-if="filteredKuruls.length === 0" class="committees-empty">
          <i class="fa fa-search"></i>
          <h2>Kurul bulunamadı</h2>
          <p>Arama metnini değiştirerek tekrar deneyebilirsiniz.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import KURULS from '@/data/kuruls';

const searchTerm = ref('');
const whatsappJoinUrl = `https://wa.me/905079217404?text=${encodeURIComponent('Derneğinize websitesinden katıldım, dernek çalışmalarında gönüllü olmak istiyorum.')}`;

const representativeCount = computed(() => (
  KURULS.filter((kurul) => kurul.isim.toLocaleLowerCase('tr-TR').includes('temsil')).length
));

const filteredKuruls = computed(() => {
  const term = searchTerm.value.toLocaleLowerCase('tr-TR');

  if (!term) {
    return KURULS;
  }

  return KURULS.filter((kurul) => {
    return [
      kurul.isim,
      kurul.baskan,
      kurul.iletisim,
      ...(kurul.ekip || []),
      ...(kurul.gorevler || [])
    ].join(' ').toLocaleLowerCase('tr-TR').includes(term);
  });
});

function normalizePhone(phone) {
  return String(phone || '').replace(/\s/g, '');
}

function normalizeName(name) {
  return String(name || '')
    .split(' ')
    .filter(Boolean)
    .map((part) => {
      if (part.includes('-')) {
        return part.split('-').map((piece) => normalizeName(piece)).join(' - ');
      }
      if (part.length <= 3 && part === part.toUpperCase()) return part;
      return part.charAt(0).toLocaleUpperCase('tr-TR') + part.slice(1).toLocaleLowerCase('tr-TR');
    })
    .join(' ');
}

function getCommitteeType(name) {
  return name.toLocaleLowerCase('tr-TR').includes('temsil')
    ? 'İlçe temsil kurulu'
    : 'Çalışma kurulu';
}
</script>

<style scoped>
.committees-page {
  background: linear-gradient(180deg, #FFFFFF 0%, #F7FAFC 48%, #FFFFFF 100%);
}

.committees-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 240px;
  gap: 32px;
  align-items: end;
  margin-bottom: 28px;
}

.committees-hero h1 {
  color: var(--dark);
  font-size: clamp(34px, 4.4vw, 58px);
  line-height: 1.06;
  max-width: 920px;
  margin: 0 0 18px;
}

.committees-hero p {
  color: #5B6475;
  font-size: 18px;
  line-height: 1.75;
  max-width: 820px;
  margin: 0;
}

.committees-summary {
  background: var(--dark);
  color: #FFFFFF;
  min-height: 190px;
  padding: 26px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 8px;
  box-shadow: 0 18px 38px rgba(15, 23, 43, .14);
}

.committees-summary span {
  color: var(--primary);
  font-size: 58px;
  font-weight: 800;
  line-height: 1;
}

.committees-summary strong {
  font-size: 20px;
  margin-top: 10px;
}

.committees-summary small {
  color: rgba(255, 255, 255, .76);
  margin-top: 8px;
}

.committees-toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
}

.committees-search {
  flex: 1;
  min-height: 56px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #FFFFFF;
  border: 1px solid rgba(15, 23, 43, .1);
  border-radius: 8px;
  padding: 0 18px;
  box-shadow: 0 10px 26px rgba(15, 23, 43, .06);
}

.committees-search i {
  color: var(--primary);
}

.committees-search input {
  width: 100%;
  border: 0;
  outline: 0;
  color: var(--dark);
  background: transparent;
  font-size: 16px;
}

.committees-toolbar .btn {
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  padding: 12px 20px;
}

.committees-feature-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 26px;
}

.committees-feature-grid article,
.committee-card,
.committees-empty {
  background: #FFFFFF;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(15, 23, 43, .06);
}

.committees-feature-grid article {
  padding: 24px;
}

.committees-feature-grid i {
  color: var(--primary);
  font-size: 30px;
  margin-bottom: 15px;
}

.committees-feature-grid h2 {
  color: var(--dark);
  font-size: 26px;
  margin-bottom: 8px;
}

.committees-feature-grid p,
.committees-empty p {
  color: #606A7A;
  line-height: 1.7;
  margin: 0;
}

.committees-list {
  display: grid;
  gap: 16px;
}

.committee-card {
  border-left: 4px solid var(--primary);
  padding: 24px;
}

.committee-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 16px;
}

.committee-type {
  display: inline-flex;
  color: #7A4C00;
  background: #FFF4DF;
  border: 1px solid #FFE2AA;
  font-size: 12px;
  font-weight: 800;
  padding: 7px 10px;
  border-radius: 999px;
  margin-bottom: 10px;
}

.committee-card h2 {
  color: var(--dark);
  font-size: 26px;
  margin: 0;
}

.committee-phone {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--dark);
  background: #F8FAFC;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 999px;
  font-weight: 800;
  padding: 9px 12px;
  white-space: nowrap;
}

.committee-phone i {
  color: var(--primary);
}

.committee-lead {
  display: grid;
  grid-template-columns: 30px 140px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  background: #F8FAFC;
  border: 1px solid rgba(15, 23, 43, .06);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 18px;
}

.committee-lead i {
  color: var(--primary);
}

.committee-lead span {
  color: #697386;
  font-weight: 700;
}

.committee-lead strong {
  color: var(--dark);
  overflow-wrap: anywhere;
}

.committee-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 20px;
}

.committee-body h3 {
  color: var(--dark);
  font-size: 18px;
  margin-bottom: 10px;
}

.committee-body ul {
  color: #566174;
  line-height: 1.7;
  margin: 0;
  padding-left: 18px;
}

.committee-members div {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.committee-members span {
  color: #344054;
  background: #F8FAFC;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 999px;
  font-weight: 700;
  padding: 8px 10px;
}

.committees-empty {
  padding: 34px;
  text-align: center;
}

.committees-empty i {
  color: var(--primary);
  font-size: 34px;
  margin-bottom: 16px;
}

@media (max-width: 991.98px) {
  .committees-hero,
  .committees-feature-grid,
  .committee-body {
    grid-template-columns: 1fr;
  }

  .committees-summary {
    min-height: auto;
  }
}

@media (max-width: 575.98px) {
  .committees-page {
    padding-bottom: 86px !important;
  }

  .committees-hero h1 {
    font-size: 32px;
  }

  .committees-hero p {
    font-size: 16px;
  }

  .committees-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .committees-toolbar .btn {
    justify-content: center;
    width: 100%;
  }

  .committee-card {
    padding: 18px;
  }

  .committee-card-head {
    flex-direction: column;
  }

  .committee-phone {
    width: 100%;
    justify-content: center;
  }

  .committee-lead {
    grid-template-columns: 28px minmax(0, 1fr);
  }

  .committee-lead strong {
    grid-column: 2;
  }
}
</style>
